// Numeric PIN keypad. Big tappable buttons for staff sign-in. Controlled
// by (value, onChange); `onComplete` fires once value reaches
// `maxLength`. No external deps — just a 3×4 grid.
//
// Does not display the entered PIN (the parent decides how/whether to
// render dots or digits). This component is only the keypad.

import type { ReactNode } from "react";

type Digit = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9";
type Key = Digit | "delete" | "clear";

const layout: Key[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "delete"];

export type PinPadProps = {
  value: string;
  onChange: (next: string) => void;
  maxLength?: number;
  onComplete?: (value: string) => void;
};

export function PinPad({ value, onChange, maxLength = 6, onComplete }: PinPadProps) {
  const handleKey = (key: Key) => {
    if (key === "delete") {
      onChange(value.slice(0, -1));
      return;
    }
    if (key === "clear") {
      onChange("");
      return;
    }
    if (value.length >= maxLength) return;
    const next = value + key;
    onChange(next);
    if (next.length === maxLength && onComplete) onComplete(next);
  };

  return (
    <fieldset className="grid grid-cols-3 gap-x-6 gap-y-5 border-0 m-0 p-0 place-items-center">
      <legend className="sr-only">PIN keypad</legend>
      {layout.map((key) => {
        if (key === "clear" && !value) {
          return <span key="clear-empty" aria-hidden="true" />;
        }
        return (
          <PadKey
            key={key}
            label={labelFor(key)}
            onPress={() => handleKey(key)}
            variant={key === "delete" || key === "clear" ? "secondary" : "primary"}
          />
        );
      })}
    </fieldset>
  );
}

function labelFor(key: Key): ReactNode {
  if (key === "delete") {
    return (
      <span role="img" aria-label="Delete last digit">
        <svg
          width="26"
          height="20"
          viewBox="0 0 26 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 1h15a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H8L1 10l7-9Z" />
          <line x1="12" y1="6" x2="20" y2="14" />
          <line x1="20" y1="6" x2="12" y2="14" />
        </svg>
      </span>
    );
  }
  if (key === "clear") return <span className="text-[15px] font-normal">Clear</span>;
  return key;
}

function PadKey({
  label,
  onPress,
  variant,
}: {
  label: ReactNode;
  onPress: () => void;
  variant: "primary" | "secondary";
}) {
  const cls =
    variant === "primary"
      ? "bg-white text-slate-900 shadow-[0_1px_1px_rgba(0,0,0,0.04),0_0_0_0.5px_rgba(0,0,0,0.04)] active:bg-slate-100"
      : "bg-transparent text-slate-500 active:bg-slate-200/60";
  return (
    <button
      type="button"
      onClick={onPress}
      className={`${cls} min-h-16 h-[76px] w-[76px] rounded-full text-[32px] font-light tabular-nums grid place-items-center transition active:scale-[0.96]`}
    >
      {label}
    </button>
  );
}
